/* In-app messaging API client (user ↔ user).
   - REST helpers wrap the /api/messaging/* endpoints.
   - openMessagingStream() opens an EventSource SSE connection that the
     client keeps alive while the user is signed in; each event is JSON
     parsed and handed to the registered callback. */
import { api } from "./api";
import { getUserToken } from "./userApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const startDm = ({ target_user_id, target_slug }) =>
  api
    .post("/messaging/conversations/start", { target_user_id, target_slug })
    .then((r) => r.data);

/* Permanently remove the caller from a conversation. If they were the
   only remaining member, the thread + its messages + read-receipts are
   wiped from MongoDB — there is NO undo. */
export const deleteConversation = (conversationId) =>
  api.delete(`/messaging/conversations/${conversationId}`).then((r) => r.data);

/* Tell the backend which conversation we're actively viewing — so
   message send won't fire bell + push for us when the other party messages
   us in this same room. Pass `null` when leaving. Safe to ignore failures
   (purely an optimisation). */
export const setPresence = (conversation_id) =>
  api
    .post("/messaging/presence", { conversation_id })
    .then((r) => r.data)
    .catch(() => null);

export const createGroup = (title, member_ids) =>
  api
    .post("/messaging/conversations/group", { title, member_ids })
    .then((r) => r.data);

export const listConversations = () =>
  api.get("/messaging/conversations").then((r) => r.data.conversations);

export const getConversation = (id) =>
  api.get(`/messaging/conversations/${id}`).then((r) => r.data);

export const listMessages = (id, { before, limit = 50 } = {}) =>
  api
    .get(`/messaging/conversations/${id}/messages`, { params: { before, limit } })
    .then((r) => r.data);  // { messages, reads }

export const sendMessage = (id, { body, attachment }) =>
  api
    .post(`/messaging/conversations/${id}/messages`, { body, attachment })
    .then((r) => r.data);

export const markRead = (id, last_read_message_id) =>
  api
    .post(`/messaging/conversations/${id}/read`, { last_read_message_id })
    .then((r) => r.data);

export const sendTyping = (id, is_typing) =>
  api
    .post(`/messaging/conversations/${id}/typing`, { is_typing })
    .then((r) => r.data);

export const getUnreadCount = () =>
  api.get("/messaging/unread-count").then((r) => r.data.unread || 0);

/**
 * Open a Server-Sent Events stream of messaging events.
 * Returns a teardown function to close the connection.
 *
 * The browser EventSource API doesn't allow custom headers, so we pass the
 * JWT via a query parameter. Backend reads it in `get_current_user` as a
 * fallback when the Authorization header is missing.
 *
 * Resilience: three defence layers against "zombie" SSE connections
 * (readyState=OPEN but no data flowing — a KNOWN iOS Safari / Cloudflare
 * failure mode where `onerror` never fires):
 *   1. Auto-reconnect via native `es.onerror` (exponential backoff → 30s max).
 *   2. Watchdog: reconnect if no server frame arrives in 45s (backend
 *      heartbeats every 15s, so 3 missed = presumed dead).
 *   3. Visibility watcher: if the PWA is backgrounded then foregrounded
 *      and the last event is >30s old, force reconnect immediately —
 *      Safari usually kills the underlying socket during backgrounding.
 */
export function openMessagingStream(onEvent, onStatus) {
  let es = null;
  let closed = false;
  let backoff = 1000;
  let lastEventAt = Date.now();
  let watchdog = null;

  const STALE_MS = 45000;      // >3 backend heartbeats missed → dead
  const VISIBLE_STALE_MS = 30000; // stricter check on wake-from-background

  const clearWatchdog = () => {
    if (watchdog) { clearInterval(watchdog); watchdog = null; }
  };

  const forceReconnect = (reason) => {
    if (closed) return;
    onStatus?.("reconnecting");
    try { es && es.close(); } catch { /* noop */ }
    es = null;
    clearWatchdog();
    // Small delay so React can render the "reconnecting" dot before we
    // start banging the network again.
    setTimeout(connect, 250);
  };

  const startWatchdog = () => {
    clearWatchdog();
    watchdog = setInterval(() => {
      if (Date.now() - lastEventAt > STALE_MS) {
        forceReconnect("stale");
      }
    }, 10000);
  };

  const connect = () => {
    if (closed) return;
    const token = getUserToken();
    if (!token) {
      onStatus?.("unauthenticated");
      return;
    }
    const url = `${BACKEND_URL}/api/messaging/stream?token=${encodeURIComponent(token)}`;
    es = new EventSource(url, { withCredentials: false });

    es.onopen = () => {
      backoff = 1000;
      lastEventAt = Date.now();
      onStatus?.("open");
      startWatchdog();
    };
    es.onmessage = (evt) => {
      lastEventAt = Date.now();
      try {
        const data = JSON.parse(evt.data);
        // Swallow heartbeats — they exist purely to feed the watchdog.
        if (data?.type === "heartbeat") return;
        onEvent?.(data);
      } catch (e) {
        /* ignore malformed frames */
      }
    };
    es.onerror = () => {
      onStatus?.("reconnecting");
      try { es.close(); } catch { /* noop */ }
      clearWatchdog();
      if (closed) return;
      setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 30000);
    };
  };

  // Wake-from-background handler — Safari on iOS almost always kills the
  // SSE socket when the PWA is minimised for more than a minute or two.
  // On foreground we reconnect if the last event is stale.
  const onVisibility = () => {
    if (document.hidden || closed) return;
    if (Date.now() - lastEventAt > VISIBLE_STALE_MS) {
      forceReconnect("wake-stale");
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }

  connect();

  return () => {
    closed = true;
    clearWatchdog();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibility);
    }
    try { es && es.close(); } catch { /* noop */ }
  };
}
